import type { KpiSeverity, KpiThreshold, WeekKpi } from './types';

export const evaluateKpiSeverity = (value: number, threshold: KpiThreshold): KpiSeverity => {
  // Nothing planned is never a problem. Without this, a 0-capacity profession
  // (no staff configured yet) would WARN on 0 ≥ 0 − warningMargin.
  if (value === 0) return 'OK';
  if (value > threshold.limit) return 'EXCEEDED';
  if (
    threshold.warningMargin !== undefined &&
    value >= threshold.limit - threshold.warningMargin
  ) {
    return 'WARNING';
  }
  return 'OK';
};

// Whether the week's admitted patients cannot all be given a room: either more
// admitted seats are planned than the location has rooms, or the booked
// patients' room needs cannot all be met at once. An overbooked property always
// shows up as a conflict too, so the property rows need no separate check.
// Shared with the bed chip, so the red chip and the week's corner icon can never
// disagree about what counts as overbooked.
export const isBedsExceeded = (kpi: WeekKpi): boolean =>
  (kpi.bedsThreshold !== undefined &&
    evaluateKpiSeverity(kpi.bedsCount, kpi.bedsThreshold) === 'EXCEEDED') ||
  (kpi.roomNeeds !== undefined && kpi.roomNeeds.conflictingPatients.length > 0);

// Beds never escalate to WARNING: treatment plans are normally fully booked
// (a location with 8 rooms planning 8 admitted patients is expected), so the
// bed threshold carries no warningMargin and only overbooking contributes.
// Planning issues escalate to EXCEEDED because they're already rendered as red
// error tags in StatusRow — the corner warning icon should match.
// A profession without a threshold (capacity unknown) contributes nothing, and
// neither does an unknown room count.
export const aggregateWeekSeverity = (kpi: WeekKpi): KpiSeverity => {
  const anyExceeded =
    kpi.professions.some(
      (p) => p.threshold && evaluateKpiSeverity(p.planned, p.threshold) === 'EXCEEDED'
    ) ||
    isBedsExceeded(kpi) ||
    kpi.planningIssues.length > 0;
  if (anyExceeded) return 'EXCEEDED';
  const anyWarning = kpi.professions.some(
    (p) => p.threshold && evaluateKpiSeverity(p.planned, p.threshold) === 'WARNING'
  );
  return anyWarning ? 'WARNING' : 'OK';
};
